import React from 'react';
import { Row, Col, Card, Typography, Tag } from 'antd';
import {
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  ThunderboltOutlined,
  RobotOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;

const AutomationDashboard = ({ stats, isLoading }) => {
  const { t } = useTranslation(['tables']);
  const pa = (key) => t(`payrollAutomation.${key}`, { ns: 'tables' });

  const statCards = [
    {
      title: pa('submissions'),
      value: stats?.totalExecutions || 0,
      icon: <ThunderboltOutlined style={{ fontSize: 24, color: '#1890ff' }} />,
      color: '#1890ff',
    },
    {
      title: pa('statusCompleted'),
      value: stats?.successfulExecutions || 0,
      icon: <CheckCircleOutlined style={{ fontSize: 24, color: '#52c41a' }} />,
      color: '#52c41a',
      suffix: stats?.totalExecutions
        ? `(${Math.round((stats.successfulExecutions / stats.totalExecutions) * 100)}%)`
        : '',
    },
    {
      title: pa('statusCancelled'),
      value: stats?.failedExecutions || 0,
      icon: <CloseCircleOutlined style={{ fontSize: 24, color: '#ff4d4f' }} />,
      color: '#ff4d4f',
    },
    {
      title: pa('inProgress'),
      value: stats?.runningExecutions || 0,
      icon: <SyncOutlined spin style={{ fontSize: 24, color: '#faad14' }} />,
      color: '#faad14',
    },
    {
      title: pa('employees'),
      value: stats?.activeWorkflows || 0,
      icon: <RobotOutlined style={{ fontSize: 24, color: '#722ed1' }} />,
      color: '#722ed1',
    },
    {
      title: pa('statusDraft'),
      value: stats?.pendingActions || 0,
      icon: <ClockCircleOutlined style={{ fontSize: 24, color: '#fa8c16' }} />,
      color: '#fa8c16',
    },
  ];

  return (
    <div style={{ marginBottom: 24 }}>
      <Title level={4} style={{ marginBottom: 16 }}>
        {pa('automationOverview')}
      </Title>

      <Row gutter={[16, 16]}>
        {statCards.map((stat, index) => (
          <Col xs={24} sm={12} md={8} lg={4} key={index}>
            <Card
              loading={isLoading}
              style={{
                borderRadius: 8,
                borderLeft: `4px solid ${stat.color}`,
              }}
              bodyStyle={{ padding: 16 }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div>{stat.icon}</div>
                <div style={{ flex: 1 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {stat.title}
                  </Text>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                    <Text strong style={{ fontSize: 20 }}>
                      {stat.value}
                    </Text>
                    {stat.suffix && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {stat.suffix}
                      </Text>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Active Rules Summary */}
      {stats?.activeRules && stats.activeRules.length > 0 && (
        <Card
          title={pa('activeAutomationRules')}
          style={{ marginTop: 16, borderRadius: 8 }}
          loading={isLoading}
        >
          <Row gutter={[16, 16]}>
            {stats.activeRules.map((rule, index) => (
              <Col xs={24} sm={12} md={8} key={index}>
                <div
                  style={{
                    padding: 12,
                    borderRadius: 6,
                    backgroundColor: '#f5f5f5',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 8,
                    }}
                  >
                    <Text strong>{rule.name}</Text>
                    <Tag color={rule.enabled ? 'green' : 'default'}>
                      {rule.enabled ? pa('enabled') : pa('disabled')}
                    </Tag>
                  </div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {pa('trigger')}: {rule.triggerType}
                  </Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {pa('lastRun')}: {rule.lastRun || pa('never')}
                  </Text>
                </div>
              </Col>
            ))}
          </Row>
        </Card>
      )}

      {/* Recent Submissions */}
      {stats?.recentActivity && stats.recentActivity.length > 0 && (
        <Card
          title={pa('recentSubmissions')}
          style={{ marginTop: 16, borderRadius: 8 }}
          loading={isLoading}
        >
          <div style={{ maxHeight: 300, overflowY: 'auto' }}>
            {stats.recentActivity.map((activity, index) => (
              <div
                key={index}
                style={{
                  padding: 12,
                  borderBottom: index < stats.recentActivity.length - 1 ? '1px solid #f0f0f0' : 'none',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <Text strong>{pa('period')}: {activity.ruleType}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {activity.timestamp}
                    </Text>
                  </div>
                  <Tag
                    color={
                      activity.status === 'SUCCESS'
                        ? 'success'
                        : activity.status === 'FAILED'
                        ? 'error'
                        : 'processing'
                    }
                  >
                    {activity.status === 'SUCCESS' ? pa('statusCompleted') :
                     activity.status === 'FAILED' ? pa('statusCancelled') :
                     pa('inProgress')}
                  </Tag>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default AutomationDashboard;
